import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
// eslint-disable-next-line import/no-unresolved
import { Card } from "@astrana/ui/base";
// eslint-disable-next-line import/no-unresolved
import { PanelCardFooter, PanelShowMoreLink } from "@astrana/ui/modules";
import { DatePicker } from "../components/DatePicker";
import {
  MemberResultsTable,
  type SearchFilters,
} from "../members/MemberResultsTable";
import { useMemberSearch } from "../members/useMemberSearch";
import { validateMemberSearch } from "../members/memberSearchValidation";

/**
 * Member search, hosted as a Home widget.
 *
 * WHY this exists alongside `MemberSearchPage`: Home's other two widgets
 * (UrgentPatientsWidget / HospitalEdEventsWidget) both render nothing without
 * a Provider Portal panel, so before a clinician links their PP account Home
 * was a heading over empty space. Search is the one thing that works for
 * every user on day one, so it becomes Home's floor — the page always has
 * something to do.
 *
 * This is NOT a re-mount of `MemberSearchPage`, deliberately. That page
 * carries the Elation/EHR bridge — `useElationBridge`, the sticky chart
 * scrape, `useResolvedMember`, and the auto-search → auto-navigate chain
 * guarded by the module-level `autoNav` singleton. A second mount of that
 * logic would race the same guard and could yank a user off Home into a
 * member profile. So this widget re-implements only the parts that are safe
 * to duplicate: the form, the 2-char/MPI validation rule, `useMemberSearch`,
 * and result navigation. Everything EHR-shaped stays on the page.
 *
 * The FORM is the page's form — same four fields in the same 2×2 order, same
 * uppercase labels, same ineligible toggle (which re-runs the current search
 * on flip), same Clear/Search pair — rendered at widget scale so the card
 * stays a plausible Home neighbour rather than twice its siblings' height.
 * The scale lives in `fieldClass` / `Field` below; the STRUCTURE is meant to
 * track the page, so change both together.
 *
 * RESULTS are `MemberResultsTable` itself — the page's own row markup, not a
 * condensed re-render of it. Same reasoning as the form: one row shape for
 * member search, in one file. It carries name/age/sex, the IPA and health-plan
 * pills, and the DOB/MPI/LOB meta grid, and it owns the row-click navigation
 * (the `pp:` branch, the `_pp` payload, `tabTitle`), so this widget no longer
 * duplicates that handler. Only its two page-scale gate states are bypassed:
 * its `!hasSearched` prompt (a `py-16` dashed panel) and its no-results copy
 * are replaced below with one compact line, because at widget scale they
 * would dwarf the form above them. Its loading skeletons are kept.
 *
 * Layout note: no `sm:min-h-[24rem]` floor like the panel cards. The card
 * starts at form height and grows with results up to `RESULTS_MAX_H`, past
 * which the list scrolls in place rather than running the card down the page.
 */

/** Compact field styling — the page's `Field` input at widget scale. */
const fieldClass =
  "min-w-0 rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground " +
  "placeholder:text-muted-foreground hover:border-ring focus:border-primary focus:outline-none " +
  "focus:ring-2 focus:ring-primary/15 transition-all duration-150";

/** Compact label styling — same treatment as the page, one step down. */
const labelClass =
  "text-[10px] font-semibold uppercase tracking-wide text-foreground/80";

/**
 * Scroll cap for the results list, sized to show FOUR full rows.
 *
 * A `MemberResultsTable` row now measures ~68px (`py-2.5` = 20, identity line
 * ~24, `gap-1` = 4, one meta line ~20), separated by a 1px divider rather
 * than a 12px gap — the rows lost their nested-card chrome. Four rows is
 * therefore ~276, plus the table's own "N results" count line (~20) and its
 * gap (12) = ~308 (~19rem).
 *
 * Set to 22rem (352px), not trimmed to 19: four rows is the floor asked for,
 * not the target, so this shows a fifth and absorbs a row whose IPA/plan
 * pills wrap onto a second line. It is a MAX, so one or two results still
 * render at their natural height and the card stays short.
 *
 * Re-measure if the row markup in MemberResultsTable.tsx changes.
 */
const RESULTS_MAX_H = "max-h-[22rem]";

const EMPTY_FILTERS: SearchFilters = {
  firstName: "",
  lastName: "",
  mpi: "",
  dob: "",
};

export default function MemberSearchWidget() {
  const navigate = useNavigate();
  const { search, results, loading, error, requiresDob } = useMemberSearch();
  const [filters, setFilters] = useState<SearchFilters>(EMPTY_FILTERS);
  const [submitted, setSubmitted] = useState<SearchFilters | null>(null);
  const [showIneligible, setShowIneligible] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const updateField = (key: keyof SearchFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setValidationError(null);
  };

  const runSearch = (next: SearchFilters, ineligible: boolean) =>
    void search({
      firstName: next.firstName.trim() || undefined,
      lastName: next.lastName.trim() || undefined,
      mpi: next.mpi.trim() || undefined,
      dob: next.dob.trim() || undefined,
      showIneligibleMembers: ineligible,
    });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Same gate as MemberSearchPage's `onSubmit` — see memberSearchValidation.
    const invalid = validateMemberSearch(filters, { requiresDob });
    if (invalid) {
      setValidationError(invalid);
      return;
    }
    setValidationError(null);
    setSubmitted(filters);
    runSearch(filters, showIneligible);
  };

  const onClear = () => {
    setFilters(EMPTY_FILTERS);
    setSubmitted(null);
    setValidationError(null);
  };

  /**
   * Hand the current search to the full page, which restores it from route
   * state (`MemberSearchPage` reads `location.state.searchFilters`). Now that
   * the header no longer carries a Search tab, this link and a member profile
   * are the only ways a user reaches that page, so it renders whenever there
   * are results rather than only on overflow.
   */
  const openFullSearch = () => {
    navigate("/members", {
      state: submitted ? { searchFilters: submitted } : undefined,
    });
  };

  const hasSearched = submitted !== null;

  return (
    // `display: contents` scopes the library's `important: ".astrana-ui"`
    // utilities to this subtree without inserting a layout box, so `Card`
    // stays the Home grid's direct item — same wrapper the panel cards use
    // internally, which bare `Card`/`PanelList` don't supply themselves.
    <div className="astrana-ui" style={{ display: "contents" }}>
      <Card
        variant="plain"
        header={
          <>
            <Search aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span>Member Search</span>
            {hasSearched && !loading && results.length > 0 ? (
              <span className="ml-auto text-xs font-normal text-muted-foreground">
                {results.length} {results.length === 1 ? "match" : "matches"}
              </span>
            ) : null}
          </>
        }
      >
        {/* `group` pairs with the submit button's `group-focus-within:*`
            classes below: Search sits quiet (outline) until the user is
            actually engaged with this form, then promotes to the primary
            fill. See that button for why focus-within is the right hook. */}
        <form
          onSubmit={onSubmit}
          className="group flex flex-col gap-3"
          aria-describedby={validationError ? "home-search-error" : undefined}
        >
          {/* The search rule stated UP FRONT. `buildFilter` drops a lone
              first or last name, so a half-filled name searches on nothing —
              the same rule the validation error below repeats on a failed
              submit, said once before the user can trip on it. Provider
              Portal logins also need a DOB with a name (validateMemberSearch). */}
          <p className="m-0 text-xs text-muted-foreground">
            {requiresDob
              ? "Enter a first and last name (2+ characters each) with a Date of Birth, or search by Member ID."
              : "Enter a first and last name (2+ characters each), or search by Member ID."}
          </p>

          {/* 2x2 while the card is column-width, one 4-across row once Home
              gives it the full page. `lg:` is the SAME breakpoint HomePage's
              lower grid splits into columns at, so the card widens and these
              fields spread in one step — at full width a 2x2 leaves each
              field ~half the page, which reads as a stretched form rather
              than a search bar. Reconsider if this widget is ever mounted
              somewhere narrower than Home's full-width row. */}
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            <Field
              id="home-ms-firstName"
              label="First Name"
              placeholder="e.g. John"
              value={filters.firstName}
              onChange={(v) => updateField("firstName", v)}
            />
            <Field
              id="home-ms-lastName"
              label="Last Name"
              placeholder="e.g. Doe"
              value={filters.lastName}
              onChange={(v) => updateField("lastName", v)}
            />
            <Field
              id="home-ms-mpi"
              label="Member ID"
              placeholder="Health Plan ID or MBI"
              value={filters.mpi}
              onChange={(v) => updateField("mpi", v)}
            />
            <div className="flex flex-col gap-1">
              <label htmlFor="home-ms-dob" className={labelClass}>
                Date of Birth
                {requiresDob ? (
                  <span aria-hidden="true" className="text-destructive">
                    {" "}*
                  </span>
                ) : null}
              </label>
              {/* `size="compact"` matches the fields beside it. The calendar
                  panel is portaled to the body — without that it would be
                  clipped by `Card`'s `overflow-hidden` rounded edge. */}
              <DatePicker
                id="home-ms-dob"
                size="compact"
                value={filters.dob}
                onChange={(v) => updateField("dob", v)}
                ariaLabel="Date of Birth"
              />
            </div>
          </div>

          {/* Toggle and buttons PAIR UP into one row at `lg:`, the width at
              which Home hands this card the full page. Stacked, a
              `justify-between` toggle would strand its switch ~1000px from
              its own label at the far right edge, with the buttons orphaned
              on a row below. Below `lg:` this wrapper is inert and both keep
              exactly their column-width stacking. */}
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            {/* Same control as the page, minus its second explanatory line —
                the label carries it at this scale. Flipping it re-runs the
                CURRENT search immediately (page parity) so the row set updates
                without a second click; with nothing submitted yet it just
                arms the next search.

                The switch leads and the label follows, joined by a small gap
                and `justify-start` at EVERY width. This used to be
                `justify-between` (with `lg:justify-start`), which let the row
                grow to its container and flung the switch to the far right
                edge, stranding it a whole card-width away from the label it
                belongs to — the wider the card, the worse the separation.
                Keeping them adjacent means the control reads as one unit no
                matter how wide Home's search card gets. */}
            <div className="flex items-center justify-start gap-2">
              <button
                type="button"
                role="switch"
                aria-checked={showIneligible}
                aria-label="Show ineligible members"
                onClick={() => {
                  const next = !showIneligible;
                  setShowIneligible(next);
                  if (submitted) runSearch(submitted, next);
                }}
                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${showIneligible ? "bg-primary" : "bg-input"}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-background shadow transition-transform ${showIneligible ? "translate-x-[1.125rem]" : "translate-x-0.5"}`}
                />
              </button>
              <span className="text-xs font-medium text-foreground">
                Show ineligible members
              </span>
            </div>

            {validationError ? (
              <p id="home-search-error" className="m-0 text-xs text-destructive" role="alert">
                {validationError}
              </p>
            ) : null}
            {error ? (
              <p className="m-0 text-xs text-destructive" role="alert">
                Search failed. Please try again.
              </p>
            ) : null}

            <div className="flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={onClear}
                className="rounded-md px-2.5 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                Clear
              </button>
              {/* Quiet (outline) at rest, primary once the form is engaged.
                  Driven purely by the parent form's `group` + `focus-within`
                  — no state, no re-render.

                  `focus-within` on the FORM (not the inputs) is what makes
                  this hold together: the button is itself inside the form, so
                  pressing it — which blurs the input and focuses the button —
                  keeps the group focused. Keying off the inputs alone would
                  drop the button back to outline on mousedown, right as the
                  user commits to clicking it.

                  The border stays in both states and merely goes transparent
                  when promoted, so the button never changes size. */}
              <button
                type="submit"
                disabled={loading}
                className="inline-flex shrink-0 items-center justify-center rounded-md border border-border bg-transparent px-3.5 py-1.5 text-sm font-semibold text-foreground transition-colors hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60 group-focus-within:border-transparent group-focus-within:bg-primary group-focus-within:text-primary-foreground group-focus-within:hover:bg-primary/90"
              >
                {loading ? "Searching…" : "Search"}
              </button>
            </div>
          </div>
        </form>

        {/* Results. NOT full-bleed like the panel cards' hairline rows:
            `MemberResultsTable` renders each result as its own bordered,
            rounded card, so these want the body's normal inset — bleeding
            them to the edge would push their borders under `Card`'s own.
            One hairline separates them from the form above.

            The list scrolls INSIDE the card past `RESULTS_MAX_H`.
            `overscroll-contain` stops a flick at the end of the list from
            chaining into Home's own scroll container, and the footer link
            sits OUTSIDE the scroll box so it stays reachable at any depth. */}
        {hasSearched ? (
          <div className="mt-3 border-t border-border pt-3">
            {loading || results.length > 0 ? (
              <div className={`${RESULTS_MAX_H} overflow-y-auto overscroll-contain pr-1`}>
                <MemberResultsTable
                  results={results}
                  loading={loading}
                  hasSearched={hasSearched}
                  searchFilters={submitted ?? undefined}
                />
              </div>
            ) : (
              <p className="m-0 py-1 text-[12.5px] text-muted-foreground">
                No members found. Try adjusting your search.
              </p>
            )}
            {results.length > 0 && !loading ? (
              <PanelCardFooter>
                <PanelShowMoreLink
                  onClick={openFullSearch}
                  label="Open in full search"
                />
              </PanelCardFooter>
            ) : null}
          </div>
        ) : null}
      </Card>
    </div>
  );
}

/** Label + input pair, the page's `Field` at widget scale. */
function Field({
  id,
  label,
  placeholder,
  value,
  onChange,
}: {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={fieldClass}
      />
    </div>
  );
}
